import type { ReactNode } from 'react';
import { createContext, useRef, useState, useEffect, useMemo } from 'react';
import { Socket } from 'phoenix';

import { createSuccessNotification, createWarningNotification } from 'app/components/Notification';
import { hasAccessToFeature } from 'app/utils/permissions';

interface Props<T> {
  children: ReactNode;
  url: string;
  params: T;
  userType: UserType;
  displayCompetitors: boolean;
}

interface HashiWsContextValues {
  socket: Socket | null;
  status: 'open' | 'close';
}

const HashiWsContext = createContext<HashiWsContextValues>({
  socket: null,
  status: 'close'
});

const HashiWsProvider = <T extends object>({
  url,
  params,
  userType,
  children,
  displayCompetitors
}: Props<T>) => {
  const socket = useMemo(() => new Socket(url, { params }), []);

  const initialized = useRef<boolean>(false);

  const [status, setStatus] = useState<'open' | 'close'>('close');

  const handleOpen = () => {
    console.info('Hashi Web Socket open');

    // avoid to send notification on initial connection
    if (initialized.current) {
      // send notification only to pandascore traders
      if (hasAccessToFeature('COMPETITORS', userType)) {
        createSuccessNotification('hashi-websocket_connected_notif');
      }
    } else {
      initialized.current = true;
    }

    setStatus('open');
  };

  const handleError = () => {
    console.warn('Hashi Web Socket error');
    setStatus('close');
  };

  const handleClose = () => {
    console.log('Hashi Web Socket close');

    // send notification only to pandascore traders
    if (status !== 'close' && hasAccessToFeature('COMPETITORS', userType) && displayCompetitors) {
      createWarningNotification('hashi-websocket_disconnected_notif');
    }

    setStatus('close');
  };

  useEffect(() => {
    socket.onOpen(handleOpen);
    socket.onError(handleError);
    socket.onClose(handleClose);

    // We avoid to connect to WS if we are not Pandascore or odds are not display
    if (hasAccessToFeature('COMPETITORS', userType) && displayCompetitors) {
      socket.connect();
    }
  }, []);

  useEffect(() => {
    if (
      !!socket &&
      hasAccessToFeature('COMPETITORS', userType) &&
      displayCompetitors &&
      status === 'close'
    ) {
      socket.connect();
    }

    if (
      !!socket &&
      hasAccessToFeature('COMPETITORS', userType) &&
      !displayCompetitors &&
      status === 'open'
    ) {
      socket.disconnect();
      initialized.current = false;
    }
  }, [displayCompetitors]);

  const contextValue: HashiWsContextValues = useMemo(
    () => ({
      socket,
      status
    }),
    [status]
  );

  return <HashiWsContext.Provider value={contextValue}>{children}</HashiWsContext.Provider>;
};

HashiWsProvider.displayName = 'HashiWsProvider';

export default HashiWsProvider;
export { HashiWsContext };
